import { useState, useMemo, useCallback, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-material.css";
import {
  ColDef as BaseColDef,
  ModuleRegistry,
  RowSelectedEvent,
  RowStyleModule,
  SelectionChangedEvent,
  GridOptions,
  RowSelectionModule,
} from "ag-grid-community";

interface ColDef extends BaseColDef {
  showNist?: boolean;
}
import { ClientSideRowModelModule } from "ag-grid-community";
import { PaginationModule } from "ag-grid-community";
import {
  Box,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  useTheme,
  Typography,
  Button,
  Drawer,
  Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AppsIcon from "@mui/icons-material/Apps";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import ReactDOMServer from "react-dom/server";
import Download_Report from "../../assets/baseVersion/DownloadReportIcon.svg";
// import Download_dark from "../../assets/baseVersion/download.svg";
import Download_dark from "../../assets/baseVersion/download_icon_light.svg";
import {
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
} from "ag-grid-community";
import React from "react";
import InviteOrgDrawer from "../drawer/InviteOrgDrawer";
import SkeletonLoader from "../skeleton_loader";
import InviteSuperUser from "../drawer/InviteSuperUser";
import CreatePermissionDrawer from "../drawer/CreatePermissionDrawer";
import { useAuth } from "../../services/auth/AuthContext";
// import { SkeletonLoader } from "../../modules/dashboard/shared";

// Register required modules
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  PaginationModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  RowStyleModule,
  RowSelectionModule,
]);

interface AgGridTableProps {
  columnDefs: ColDef[];
  data: any[];
  showSearch?: boolean;
  searchPlaceholder?: string;
  height?: number | string;
  width?: number;
  rowSelection?: "single" | "multiple";
  isRowSelectable?: boolean;
  headerActions?: React.ReactNode;
  onRowClick?: (rowData: any) => void;
  showAppsIcon?: boolean;
  pagination?: boolean;
  gridOptions?: GridOptions;
  loading?: boolean;
  styles?: any;
  isLoading?: boolean;
  onSelectionChange?: (selectedRows: any[]) => void;
  checkboxSelection?: boolean;
  headerCheckboxSelection?: boolean;
  gridRef?: React.Ref<any>;
  handleReportButtonClick?: () => void;
  InviteTableRefresh?: () => void;
  onInviteSuccess?: () => void;
  onUpdateUserSuccess?: () => void;
}

const AgGridTable: React.FC<AgGridTableProps> = ({
  columnDefs,
  data,
  showSearch,
  height = 50,
  width,
  isRowSelectable = false,
  headerActions,
  onRowClick,
  showAppsIcon = true,
  pagination = true,
  loading = false,
  gridOptions,
  styles,
  isLoading,
  onSelectionChange,
  searchPlaceholder,
  checkboxSelection = false,
  headerCheckboxSelection = false,
  gridRef,
  handleReportButtonClick,
  InviteTableRefresh,
  onInviteSuccess,
  
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    columnDefs?.map((col) => col?.field ?? "")
  );
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
  const [selectedParentId, setSelectedParentId] = useState<string>("");
  const { user, isLoading: authLoading } = useAuth();
  console.log("user role in ag grid table:", user?.role);

  const path = window.location.pathname;

  const getButtonLabel = () => {
    if (path === "/user") return "Invite Super User";
    if (path === "/organisation") return "Invite Organization";
    if (path === "/role-permission") return "Create Permission";
    if (path === "/masterfile" || path === "/master-file") return "Upload";
    return null; // Or return a default label if needed
  };

  const label = getButtonLabel();


  useEffect(() => {
    setVisibleColumns(
      columnDefs
        .filter((col) => col?.showNist === undefined || col?.showNist !== false) // Include columns where showNist is undefined or not false
        .map((col) => col?.field ?? "") // Map remaining columns to their field names
    );
  }, [columnDefs]);

  const customIcons = {
    filter: ReactDOMServer.renderToString(
      <FilterListIcon
        style={{
          fontSize: 16,
          color: theme.palette.mode === "dark" ? "#F8F8FF" : "#666",
          opacity: 0.7,
        }}
      />
    ),
  };

  const [searchText, setSearchText] = useState("");

  const onRowClicked = useCallback(
    (event: any) => {
      const targetElement = event.event.target as HTMLElement;
      if (
        targetElement.closest("button") ||
        targetElement.closest(".ag-column-actions")
      ) {
        event.event.stopPropagation();
        return;
      }
      if (onRowClick) {
        onRowClick(event.data);
      }
    },
    [onRowClick]
  );

  const filteredData = useMemo(() => {
    if (!searchText) return data;

    const normalizedSearch = searchText.toString().toLowerCase();

    return data.filter((row) =>
      Object.values(row).some((value) => {
        if (value === null || value === undefined) return false;
        return value.toString().toLowerCase().includes(normalizedSearch);
      })
    );
  }, [searchText, data]);

  // const filteredData = useMemo(() => {
  //     if (!searchText) return data;

  //     const normalizedSearch = searchText.toString().toLowerCase();

  //     return data.filter((row) =>
  //         Object.values(row).some((value) => {
  //             if (value === null || value === undefined) return false;
  //             return value.toString().toLowerCase() === normalizedSearch;
  //         })
  //     );
  // }, [searchText, data]);

  const onRowSelected = useCallback(
    (event: RowSelectedEvent) => {
      if (!isRowSelectable) return;
      console.log(
        "row " +
          event.node.data.athlete +
          " selected = " +
          event.node.isSelected()
      );
    },
    [isRowSelectable]
  );

  const handleColumnToggle = (field: string) => {
    setVisibleColumns((prev) =>
      prev.includes(field)
        ? prev.filter((col) => col !== field)
        : [...prev, field]
    );
  };

  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent) => {
      if (!isRowSelectable) return;
      const selectedNodes = event.api.getSelectedNodes();
      const selectedData = selectedNodes.map((node: any) => node.data);
      onSelectionChange?.(selectedData);
    },
    [isRowSelectable, onSelectionChange]
  );

  const rowSelection = useMemo(() => {
    return isRowSelectable ? "multiple" : undefined;
  }, [isRowSelectable]);

  const getRowClass = (params: any) => {
    return params.node.rowIndex % 2 === 0 ? "ag-row-even" : "ag-row-odd";
  };

  const mergedGridOptions: GridOptions = useMemo(() => {
    const updatedColumnDefs = columnDefs.map((col, index) => ({
      ...col,
      checkboxSelection: index === 0 && checkboxSelection,
      domLayout: !pagination ? "100%" : undefined,
      headerCheckboxSelection: index === 0 && headerCheckboxSelection,
      icons: {
        filter: ReactDOMServer.renderToString(
          <FilterListIcon
            style={{
              fontSize: 16,
              color: theme.palette.mode === "dark" ? "#fff" : "#666",
              opacity: 0.7,
            }}
          />
        ),
      },
    }));

    return {
      ...gridOptions,
      columnDefs: updatedColumnDefs,
      rowSelection: rowSelection as any,
      onRowSelected,
      onSelectionChanged: handleSelectionChanged,
      getRowClass,
      onRowClicked,
      context: {
        openCreateDrawerWithParent: (parentId?: string) => {
          setSelectedParentId(parentId || "");
          setIsDrawerOpen(true);
        },
      },

      defaultColDef: {
        flex: 1,
        sortable: true,
        filter: true,
        icons: customIcons,
        menuTabs: ["filterMenuTab"],
        floatingFilter: false,
      },

      suppressRowClickSelection: true,
      suppressCellFocus: true,
      rowMultiSelectWithClick: false,
      suppressRowHoverHighlight: false,
      suppressRowTransform: true,
      rowClass: "ag-row-no-highlight",
      pagination,
      paginationPageSize: 50,
      headerHeight: 32,
    };
  }, [
    gridOptions,
    columnDefs,
    checkboxSelection,
    headerCheckboxSelection,
    rowSelection,
    onRowSelected,
    handleSelectionChanged,
    onRowClicked,
    pagination,
    customIcons,
  ]);

  return (
    <Box
      sx={{
        width: "100%",
        height: height,
        ...styles,
        "& .ag-theme-material .ag-row-even": {
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 5, 0.05) !important"
              : "#F8F8FF !important",
        },
        "& .ag-theme-material .ag-row-odd": {
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(0, 0, 0, 0.2) !important"
              : "#FFFFFF !important",
        },
        "& .ag-row": {
          transition: "background-color 0.2s ease",
          "&:hover": {
            backgroundColor: `${theme.palette.action.hover} !important`,
          },
        },
        "& .ag-cell": {
          transition: "background-color 0.2s ease",
          "&:hover": {
            backgroundColor: `${theme.palette.action.hover} !important`,
          },
        },
        "& .ag-header-cell.checkbox-header": {
          paddingLeft: "12px",
        },
        "& .ag-cell.checkbox-cell": {
          paddingLeft: "12px",
        },
        "& .ag-row-hover": {
          backgroundColor: `${theme.palette.action.hover} !important`,
        },
        "& .ag-row-focus": {
          backgroundColor: `${theme.palette.action.selected} !important`,
        },
        "& .ag-row-selected": {
          backgroundColor: `${theme.palette.action.selected} !important`,
        },
        "& .ag-row-no-highlight": {
          "&:hover": {
            backgroundColor: `${theme.palette.action.hover} !important`,
          },
          "&.ag-row-selected": {
            backgroundColor: `${theme.palette.action.selected} !important`,
          },
        },
        "& .ag-checkbox-input-wrapper": {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
        },
        "& .ag-checkbox-input-wrapper input[type='checkbox']": {
          width: "16px",
          height: "16px",
          cursor: "pointer",
        },
        "& .ag-checkbox-input-wrapper.ag-checked": {
          color: theme.palette.primary.main, // Changed to black color
        },
        "& .ag-checkbox-input-wrapper.ag-checked::after": {
          color: theme.palette.primary.main, // Apply black color
        },
        // Add shadow on hovered column header
        "& .ag-header-cell:hover": {
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 2px 8px 0 rgba(0,0,0,0.6)"
              : "0 2px 8px 0 rgba(0,0,0,0.15)",
          zIndex: 2,
          position: "relative",
        },
      }}
    >
      {/* Header Actions */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 0,
          width: "100%",
        }}
      >
        {/* Search Input */}
        {showSearch && (
          <Box sx={{ flexGrow: 1, p: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <TextField
                size="small"
                variant="standard"
                fullWidth
                placeholder={searchPlaceholder ? searchPlaceholder : "Search"}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchText && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchText("")}
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                  disableUnderline: true,
                }}
                sx={{
                  color: theme.palette.text.primary,
                  borderRadius: "6px",
                  mt: -1,
                  "& .MuiInputBase-input": {
                    color: theme.palette.text.primary,
                  },
                  height: 24,
                  "& fieldset": { border: "none" },
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#1A1D25" : "#fbfbff",
                }}
              />
              {path === "/organisation" && (
                <div
                  onClick={() => {
                    if (handleReportButtonClick) {
                      handleReportButtonClick();
                    }
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && handleReportButtonClick) {
                      handleReportButtonClick();
                    }
                  }}
                >
                  <img
                    src={isDarkMode ? Download_dark : Download_Report}
                    alt=""
                    style={{ height: "17px", width: "17px" }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: isDarkMode ? "#fff" : "#0F02C3",
                      fontSize: "16px",
                      fontWeight: 500,
                    }}
                  >
                    Report
                  </Typography>
                </div>
              )}
              {label && user && user.role !== "Super User Read Only" && (
                <Button
                  variant="contained"
                  sx={{ whiteSpace: "nowrap", width: "150px", p: 1 }}
                  size="small"
                  onClick={() => {
                    if (path === "/masterfile" || path === "/master-file") {
                      if (onInviteSuccess) onInviteSuccess();
                    } else {
                      setSelectedParentId("");
                      setIsDrawerOpen(true);
                    }
                  }}
                >
                  {label}
                </Button>
              )}
            </Box>
          </Box>
        )}

        {isDrawerOpen && (
          <Drawer
            anchor="right"
            open={isDrawerOpen}
            onClose={() => {
              setIsDrawerOpen(false);
              setSelectedParentId("");
            }}
            sx={{
              "& .MuiDrawer-paper": {
                width: "45%",
                overflow: "auto",
                scrollbarWidth: "thin",
              },
            }}
          >
            <Box>
              {/* Header */}
              {/* <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      sx={(theme) => ({
        position: "sticky",
        top: 0,
        left: 0,
        right: 0,
        width: "100%",
        zIndex: 13,
        bgcolor: theme.palette.mode === "dark" ? "#181818" : "#fff",
        color: theme.palette.mode === "dark" ? "#fff" : "inherit",
        minHeight: 64,
        p: 0,
        m: 0,
        borderTop: 0,
      })}
    >
<Typography
        variant="body2"
        sx={{
          fontWeight: "bold",
          fontSize: "18px",
          ml: 2,
        }}
      >
        {path === "/user"
          ? "Invite User"
          : path === "/organisation"
          ? "Invite Organization"
          : "Invite"}
      </Typography>      

      <IconButton onClick={() => setIsDrawerOpen(false)} sx={{ pr: 2 }}>
        <CloseIcon />
      </IconButton>
    </Box> */}

              {/* <Divider /> */}

              {path === "/user" ? (
                <InviteSuperUser
                  onCloseDrawer={() => setIsDrawerOpen(false)}
                  setIsDrawerOpen={setIsDrawerOpen}
                  onInviteSuccess={onInviteSuccess}
                />
              ) : path === "/organisation" ? (
                <InviteOrgDrawer
                  onCloseDrawer={() => setIsDrawerOpen(false)}
                  InviteTable={() => {
                    if (InviteTableRefresh) InviteTableRefresh();
                  }}
                />
              ) : path === "/role-permission" ? (
                <CreatePermissionDrawer
                  initialParentId={selectedParentId}
                  onCloseDrawer={() => {
                    setIsDrawerOpen(false);
                    setSelectedParentId("");
                  }}
                  onSuccess={() => {
                    if (onInviteSuccess) onInviteSuccess();
                    if (InviteTableRefresh) InviteTableRefresh();
                  }}
                />
              ) : null}
            </Box>
          </Drawer>
        )}

        {headerActions}

        {showAppsIcon && (
          <Box>
            <IconButton
              size="small"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{ "&:focus": { outline: "none" } }}
            >
              <AppsIcon />
            </IconButton>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          position: "relative",
          width: width ? width : "100%",
          height: height,
          "& .ag-header": {
            backgroundColor: isDarkMode ? "#393E4B" : "#E7E6F3",
            color: theme.palette.text.primary,
            fontWeight: "bold",
            border: "none",
          },
          "& .ag-header-cell": {
            border: "none !important",
            transition: "background-color 0.2s ease, box-shadow 0.2s ease",
            "&:hover": {
              backgroundColor: isDarkMode
                ? "#393E4B !important"
                : `${theme.palette.action.hover} !important`,
            },
          },
          "& .ag-paging-panel": {
            backgroundColor: isDarkMode ? "#393E4B" : "#FFFFFF",
            color: theme.palette.text.primary,
            fontWeight: 500,
            pb: 0,
            mb: 0,
            marginBottom: 0,
            minHeight: 36,
            height: 36,
            display: "flex",
            alignItems: "center",
            flexWrap: "nowrap",
            fontSize: "13px",
            gap: 1,
            padding: "0 12px",
            "& > *": {
              margin: "0 4px",
              padding: 0,
              lineHeight: 1.2,
            },
            "& .ag-paging-row-summary-panel": {
              margin: 0,
              padding: 0,
              fontSize: "13px",
              fontWeight: 400,
            },
            "& .ag-paging-page-summary-panel": {
              margin: 0,
              padding: 0,
              fontSize: "13px",
              fontWeight: 400,
            },
            "& .ag-paging-button": {
              // minWidth: 24,
              // minHeight: 24,
              padding: 0,
              margin: "0 2px",
              borderRadius: "4px",
              fontSize: "16px",
            },
            "& select": {
              minHeight: 24,
              fontSize: "13px",
              padding: "2px 8px",
              borderRadius: "4px",
            },
          },
          "& .ag-root-wrapper": {
            border: "none",
          },
          "& .ag-row": {
            border: "none !important",
          },
          "& .ag-cell": {
            border: "none !important",
          },
          "& .ag-header-row": {
            border: "none !important",
          },
          "& .ag-checkbox-input-wrapper": {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
          },
          "& .ag-checkbox-input-wrapper input[type='checkbox']": {
            width: "16px",
            height: "16px",
            cursor: "pointer",
          },
          "& .ag-checkbox-input-wrapper.ag-checked": {
            color: theme.palette.primary.main,
          },
          "& .ag-header-cell.checkbox-header": {
            paddingLeft: "12px",
            "& .ag-checkbox-input-wrapper": {
              marginLeft: "4px",
            },
          },
          "& .ag-cell.checkbox-cell": {
            paddingLeft: "12px",
            "& .ag-checkbox-input-wrapper": {
              marginLeft: "4px",
            },
          },
          //ag-grid scroll
          "& .ag-body-vertical-scroll-viewport": {
            "&::-webkit-scrollbar": {
              width: "4px !important",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: isDarkMode ? "#424242" : "#D3D3D3",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#B0B0B0",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: isDarkMode ? "#191C24" : "#f0f0f0",
              //backgroundColor: "red",
              borderRadius: "10px",
            },
          },
          "& .ag-body-horizontal-scroll-viewport": {
            "&::-webkit-scrollbar": {
              height: "4px !important",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: isDarkMode ? "#424242" : "#D3D3D3",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#B0B0B0",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: isDarkMode ? "#191C24" : "#f0f0f0",
              borderRadius: "10px",
            },
          },
        }}
      >
        <div
          className={`ag-theme-material ${theme.palette.mode === "dark" ? "dark-theme" : ""}`}
          style={{
            height: height,
            width: width ? width : "100%",
          }}
        >
          {isLoading ? (
            <SkeletonLoader count={25} styles={{ height: 20 }} />
          ) : (
            <AgGridReact
              // Force re-render when theme changes
              theme="legacy"
              className="ag-theme-material"
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs?.filter((col) =>
                col?.field ? visibleColumns.includes(col.field) : true
              )}
              rowModelType="clientSide"
              gridOptions={mergedGridOptions}
            />
          )}
        </div>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          PaperProps={{
            sx: {
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0px 4px 20px rgba(0, 0, 0, 0.3)"
                  : "0px 4px 20px rgba(0, 0, 0, 0.1)",
              borderRadius: 1,
              minWidth: 200,
            },
          }}
        >
          <Typography variant="h6" sx={{ padding: 1, pb: 0 }}>
            Columns
          </Typography>
          {columnDefs.map(
            (col, index) =>
              col?.headerName && (
                <MenuItem key={col.field}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          col?.field
                            ? visibleColumns.includes(col.field)
                            : false
                        }
                        onChange={() =>
                          col?.field && handleColumnToggle(col.field)
                        }
                        disabled={index === 0}
                      />
                    }
                    disabled={col.pinned == "left"}
                    label={col.headerName}
                  />
                </MenuItem>
              )
          )}
        </Menu>
      </Box>
    </Box>
  );
};

export default AgGridTable;
